// src/components/Search/S3DownloadLinksDisplay.js

import React, { useMemo } from 'react';
import { getResolution } from '../../utils/qualityUtils';
import { parseSeasonFromUrl } from '../../utils/s3UrlParser'; // Import the parsing function
import { FaDownload } from 'react-icons/fa';

export default function S3DownloadLinksDisplay({ links, selectedResult }) {
  // Use useMemo to optimize performance by recomputing only when links or selectedResult.type change
  const { movies, tvShows } = useMemo(() => {
    if (!Array.isArray(links)) return { movies: [], tvShows: {} };

    const movies = [];
    const tvShows = {};

    links.forEach((link) => {
      // Check the type from selectedResult and categorize accordingly
      if (selectedResult.type === 'tv') {
        const season = parseSeasonFromUrl(link.url);
        const seasonKey = season ? `Season ${season}` : 'Unknown Season';
        if (!tvShows[seasonKey]) {
          tvShows[seasonKey] = [];
        }
        tvShows[seasonKey].push(link);
      } else {
        movies.push(link);
      }
    });

    // Sort the seasons numerically
    const sortedTvShows = {};
    Object.keys(tvShows)
      .sort((a, b) => {
        const seasonA = parseInt(a.replace('Season ', ''), 10);
        const seasonB = parseInt(b.replace('Season ', ''), 10);
        return seasonA - seasonB;
      })
      .forEach((seasonKey) => {
        sortedTvShows[seasonKey] = tvShows[seasonKey];
      });

    return { movies, tvShows: sortedTvShows };
  }, [links, selectedResult.type]);

  // Function to render the selected result information
  const renderSelectedResult = () => (
    <div className="flex items-center space-x-6 mb-6 bg-gray-800 p-6 rounded-lg shadow-lg transition-transform transform hover:scale-105 duration-200">
      {selectedResult.photoUrl ? (
        <img
          src={selectedResult.photoUrl}
          alt={selectedResult.title || selectedResult.name}
          className="w-28 h-40 object-cover rounded-lg shadow-md"
        />
      ) : (
        <div className="w-28 h-40 bg-gray-700 flex items-center justify-center rounded-lg">
          <span className="text-sm text-gray-300">No Image</span>
        </div>
      )}
      <div className="flex flex-col text-left space-y-2">
        <h3 className="text-2xl font-semibold text-accent leading-snug">
          {selectedResult.title || selectedResult.name}
        </h3>
        <p className="text-sm text-gray-300">
          {selectedResult.year} • {selectedResult.type}
        </p>
        {selectedResult.description && (
          <p className="text-gray-400 text-sm leading-relaxed mt-2 line-clamp-3">
            {selectedResult.description.length > 200
              ? `${selectedResult.description.substring(0, 200)}...`
              : selectedResult.description}
          </p>
        )}
      </div>
    </div>
  );

  // Helper function to render download buttons
  const renderDownloadButton = (link) => {
    const displayText = `${link.quality}`;
    const resolution = getResolution(link.quality);

    // Determine badge color based on resolution
    const badgeColor =
      resolution >= 1080
        ? 'bg-green-500'
        : resolution >= 720
        ? 'bg-yellow-500'
        : 'bg-red-500';

    return (
      <a
        key={link.url}
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className="bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-2xl transition transform hover:scale-105 duration-300 flex flex-col items-start"
        aria-label={`Download ${link.quality}`}
      >
        {/* Quality and Type Badges */}
        <div className="flex items-center space-x-2">
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${badgeColor} text-white`}
          >
            {displayText}
          </span>
          {link.type && (
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-500 text-white">
              {link.type}
            </span>
          )}
        </div>
        {/* Download Icon */}
        <FaDownload className="text-accent text-3xl mt-4 self-center" />
      </a>
    );
  };

  return (
    <div className="p-6 w-full max-w-6xl mx-auto relative">
      {/* Display selected result at the top */}
      {selectedResult && renderSelectedResult()}

      <h3 className="text-3xl font-bold mb-6">Available Downloads</h3>

      {/* TV Shows Section */}
      {selectedResult.type === 'tv' && Object.keys(tvShows).length > 0 && (
        <div className="mb-8">
          {Object.keys(tvShows).map((seasonKey) => (
            <div key={seasonKey}>
              <h4 className="text-2xl font-semibold mb-4">{seasonKey}</h4>
              <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
                {tvShows[seasonKey].map((link) => renderDownloadButton(link))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Movies Section */}
      {selectedResult.type === 'movie' && movies.length > 0 && (
        <div className="mb-8">
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
            {movies.map((link) => renderDownloadButton(link))}
          </div>
        </div>
      )}
    </div>
  );
}
